import React from "react"
import { useHistory } from "react-router-dom"
import { useSnackbar } from "notistack"
import { useUserContextOption } from "../../context-options/hooks/use-user-context-option"
import { useUrlWithContext } from "../../hooks/use-url-with-context"
import { useOrganisation } from "./use-organisation"
import { useOrganisationActions } from "./use-organisation-actions"

export const useConfirmDeleteOrganisation = ({ orgId, deleteErrorMessage, deleteSuccessMessage }: Params) => {
  const history = useHistory()
  const { userContext } = useUserContextOption()
  const { organisation } = useOrganisation(orgId)
  const { deleteOrganisation } = useOrganisationActions()
  const { enqueueSnackbar } = useSnackbar()
  const { createPathWithGivenContext } = useUrlWithContext()
  const [dialogOpen, setDialogOpen] = React.useState(false)
  const [status, setStatus] = React.useState<Status>("idle")
  const [confirmationText, setConfirmationText] = React.useState("")
  const [confirmPassword, setConfirmPassword] = React.useState("")

  const isIdle = status === "idle"
  const isDeleting = status === "pending"
  const disableDeletion = !isIdle || isDeleting || confirmationText !== organisation?.name || !confirmPassword
  const closeDialog = () => setDialogOpen(false)
  const openDialog = () => setDialogOpen(true)
  const confirmAndCloseDialog = async () => {
    setStatus("pending")
    setConfirmationText("")
    setConfirmPassword("")
    try {
      const deleted = await deleteOrganisation(orgId, { password: confirmPassword })
      setStatus(deleted ? "success" : "error")
    } catch (error) {
      setStatus("error")
    }
  }

  /**
   * This effect handles when user clicks on the delete button and the delete request either succeeds or fails.
   */
  React.useEffect(() => {
    /**
     * When the status changes to error, we can assume that the organisation has not been deleted successfully.
     * We can then show an error message. -> Removing the error default message after adding password confirmation
     * And we reset the status to idle so that the user can try again.
     */
    if (status === "error") {
      setStatus("idle")
    }
  }, [
    status,
    enqueueSnackbar,
    history,
    deleteSuccessMessage,
    deleteErrorMessage,
    userContext,
    createPathWithGivenContext,
    userContext?.id,
  ])

  return {
    dialogOpen,
    closeDialog,
    openDialog,
    confirmAndCloseDialog,
    confirmationText,
    setConfirmationText,
    isDeleting,
    disableDeletion,
    confirmPassword,
    setConfirmPassword,
  }
}

type Params = {
  orgId: string
  deleteSuccessMessage: string
  deleteErrorMessage: string
}

type Status = "idle" | "success" | "pending" | "error"
